import { cssVar, Text } from '@cad-arc';
import { CSSProperties } from 'react';
import { LinkText } from '../../../../../common/components/link-text';
import { createCell, SimpleTable } from '../../../../../common/components/simple-table';
import { formatMonthDayHoursMinutes } from '../../../../../common/helpers/utils';
import { useOpenEventPanel } from '../../../../use-open-event-panel';
import { TabSection } from '../../../../../panels/entity-panels/tabs/tab-section';
import { useEventDuplicateEventSearchQueryQuery } from '../../../../event-state/use-event-duplicate-event-search-query';
import { Tooltip } from '~/common/components/tooltip';

export function DuplicateEventsSection({
    latitude,
    longitude,
    locationId,
    eventId,
}: {
    latitude?: number;
    longitude?: number;
    locationId?: number;
    eventId?: number;
}) {
    const openEventPanel = useOpenEventPanel();

    const {
        data: duplicateEventSearchResponse,
        isFetching,
    } = useEventDuplicateEventSearchQueryQuery({
        latitude,
        longitude,
        locationId,
        eventId,
    });

    return (
        <TabSection
            title={`Potential Duplicates${
                duplicateEventSearchResponse?.cadNextDuplicateEvents
                    ? ` (${duplicateEventSearchResponse.cadNextDuplicateEvents.length})`
                    : ''
            }`}
            loading={isFetching}
            testId="POTENTIAL_DUPLICATES"
        >
            {!!duplicateEventSearchResponse?.cadNextDuplicateEvents.length && (
                <SimpleTable
                    layout="auto"
                    hasBorders={false}
                    columnStyles={[
                        // All columns should only be as wide
                        // as their content, except for the third column,
                        // which will take up the remaining space
                        { whiteSpace: 'nowrap', width: '1%' },
                        { whiteSpace: 'nowrap', width: '1%' },
                        { width: '100%' },
                        { whiteSpace: 'nowrap', width: '1%' },
                    ]}
                    rows={[
                        [
                            createCell({ content: 'Dist.' }),
                            createCell({ content: 'Created' }),
                            createCell({ content: 'CFS' }),
                            createCell({ content: 'Event #' }),
                        ],
                        ...duplicateEventSearchResponse.cadNextDuplicateEvents.map(
                            (duplicateEvent) => {
                                // Cleared events have slightly different styling
                                const clearedEventStyles: CSSProperties = duplicateEvent.isActive
                                    ? {}
                                    : ({
                                          color: cssVar('arc.colors.text.placeholder'),
                                          fontStyle: 'italic',
                                          '--arc-colors-text-primary': cssVar(
                                              'arc.colors.text.placeholder'
                                          ),
                                      } as CSSProperties);

                                return [
                                    createCell({
                                        content: duplicateEvent.distance,
                                        cellStyle: clearedEventStyles,
                                    }),
                                    createCell({
                                        content: formatMonthDayHoursMinutes(
                                            duplicateEvent.receivedDateUtc
                                        ),
                                        cellStyle: clearedEventStyles,
                                    }),
                                    createCell({
                                        content: (
                                            <Tooltip overlay={duplicateEvent.callForServiceTip}>
                                                <Text>{duplicateEvent.callForService}</Text>
                                            </Tooltip>
                                        ),
                                        cellStyle: clearedEventStyles,
                                    }),
                                    createCell({
                                        content: (
                                            <LinkText
                                                onClick={() => {
                                                    openEventPanel({
                                                        eventId: duplicateEvent.id,
                                                    });
                                                }}
                                            >
                                                {duplicateEvent.agencyEventNumber}
                                            </LinkText>
                                        ),
                                        cellStyle: clearedEventStyles,
                                    }),
                                ];
                            }
                        ),
                    ]}
                />
            )}
        </TabSection>
    );
}
